/**
 * Command Parser Hook
 * 
 * Input'u parse eder, shortcut'ları resolve eder, eşleşen komutları bulur
 */

import { useSlashCommandStore } from '../store/slash-command-store';
import { commandRegistry, findCommandByTrigger } from '../registry/command-registry';
import type { ParseResult, CommandMatch, SlashCommand } from '../types/command-types';

export const useCommandParser = () => {
  const { shortcuts } = useSlashCommandStore();
  
  /**
   * Parse Input
   * 
   * Input string'ini parse eder ve command bilgilerini çıkarır
   * 
   * Example:
   * - "/cart" → { isCommand: true, commandPath: ["/cart"], query: "" }
   * - "/model/llm gpt" → { isCommand: true, commandPath: ["/model", "/model/llm"], query: "gpt" }
   * - "hello" → { isCommand: false, commandPath: [], query: "" }
   */
  const parseInput = (input: string): ParseResult => {
    // "/" ile başlamıyorsa command değil
    if (!input.startsWith('/')) {
      return {
        isCommand: false,
        commandPath: [],
        query: '',
        rawInput: input,
      };
    }
    
    // Shortcut resolve
    let resolved = input;
    Object.entries(shortcuts).forEach(([shortcut, command]) => {
      if (input.startsWith(shortcut)) {
        // Shortcut'u replace et
        // Örnek: "/c something" → "/cart something"
        resolved = input.replace(shortcut, command);
      }
    });
    
    // Split by space - ilk kısım command, geri kalanı query
    const parts = resolved.split(' ');
    const commandText = parts[0];
    const query = parts.slice(1).join(' ');
    
    // Command path'i oluştur
    // "/model/llm" → ["model", "llm"] → ["/model", "/model/llm"]
    const segments = commandText.split('/').filter(Boolean);
    const commandPath = segments.map((_, i) => 
      '/' + segments.slice(0, i + 1).join('/')
    );
    
    return {
      isCommand: true,
      commandPath,
      query,
      rawInput: input,
    };
  };
  
  /**
   * Calculate Match Score
   * 
   * Command ile query arasındaki match score'u hesaplar (0-100)
   * Sadece trigger ve shortcut'a bakılır (label/description'a değil)
   */
  const calculateMatchScore = (command: SlashCommand, query: string): number => {
    // Sadece "/" yazıldıysa tüm komutları göster
    if (query === '/' || query === '') {
      return 100;
    }
    
    // Query'den "/" çıkar - sadece komut kısmını karşılaştır
    const cleanQuery = query.startsWith('/') ? query : '/' + query;
    const lowerQuery = cleanQuery.toLowerCase();
    const lowerTrigger = command.trigger.toLowerCase();
    const commandShortcut = command.shortcut?.toLowerCase();
    
    // 1. Exact match in trigger
    if (lowerTrigger === lowerQuery) {
      return 100;
    }
    
    // 2. Exact match in shortcut
    if (commandShortcut && lowerQuery === commandShortcut) {
      return 100;
    }
    
    // 3. Trigger starts with query (en önemli match)
    if (lowerTrigger.startsWith(lowerQuery)) {
      return 95;
    }
    
    // 4. Shortcut starts with query
    if (commandShortcut && commandShortcut.startsWith(lowerQuery)) {
      return 90;
    }
    
    // 5. Query starts with trigger (kısmi match)
    if (lowerQuery.startsWith(lowerTrigger)) {
      return 85;
    }
    
    // 6. Trigger contains query (içinde geçiyor mu?)
    if (lowerTrigger.includes(lowerQuery)) {
      return 70;
    }
    
    // 7. Shortcut contains query
    if (commandShortcut && commandShortcut.includes(lowerQuery)) {
      return 65;
    }
    
    // 8. Fuzzy match kaldırıldı - sadece exact/starts with match'ler kullanılıyor
    // Bu sayede `/g` yazınca "home" gibi yanlış match'ler olmaz
    
    return 0; // No match
  };
  
  
  /**
   * Find Matching Commands
   * 
   * Input'a göre eşleşen komutları bulur ve score'a göre sıralar
   */
  const findMatchingCommands = (input: string): CommandMatch[] => {
    const { isCommand, commandPath, query } = parseInput(input);
    
    if (!isCommand) return [];
    
    // Input'u tam olarak parse et - örneğin "/cart" → commandText = "/cart"
    const commandText = input.split(' ')[0]; // İlk kısım command
    
    // Eğer path varsa, o path'teki command'ın children'larını göster
    if (commandPath.length > 0) {
      const lastPath = commandPath[commandPath.length - 1];
      const activeCommand = findCommandByTrigger(lastPath);
      
      // Exact match bulundu - o command'ı göster
      if (activeCommand && commandText === activeCommand.trigger) {
        // Parent command ise children'ları göster
        if (activeCommand.type === 'parent' && activeCommand.children) {
          return activeCommand.children.map((child) => ({
            command: child,
            matchScore: calculateMatchScore(child, query),
            matchedPath: [...commandPath, child.trigger],
          }));
        }
        
        // Dynamic command ise (children'ları dinamik yüklenecek)
        if (activeCommand.type === 'dynamic') {
          // Dynamic content hook'u tarafından yüklenecek
          return [];
        }
        
        // Exact match - sadece bu command'ı döndür
        return [{
          command: activeCommand,
          matchScore: 100,
          matchedPath: commandPath,
        }];
      }
    }
    
    // Root level - input'a göre filtrele
    // Önce shortcut'u resolve et
    let resolvedCommandText = commandText;
    Object.entries(shortcuts).forEach(([shortcut, fullCommand]) => {
      if (commandText === shortcut) {
        resolvedCommandText = fullCommand;
      }
    });
    
    const matches: CommandMatch[] = [];
    
    for (const command of commandRegistry) {
      // Shortcut match kontrolü
      if (command.shortcut && commandText === command.shortcut) {
        matches.push({
          command,
          matchScore: 100,
          matchedPath: [command.trigger],
        });
        continue;
      }
      
      // Trigger match kontrolü
      const score = calculateMatchScore(command, resolvedCommandText);
      
      // Minimum score threshold (40'dan büyük - daha strict)
      if (score >= 40) {
        matches.push({
          command,
          matchScore: score,
          matchedPath: [command.trigger],
        });
      }
    }
    
    // Score'a göre sırala (büyükten küçüğe)
    return matches.sort((a, b) => b.matchScore - a.matchScore);
  };
  
  return {
    parseInput,
    findMatchingCommands,
    calculateMatchScore,
  };
};

